'use client';

import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useGetLiveStreams from 'hooks/useGetLiveStreams';
import useGetScheduledLiveStreams from 'hooks/useGetScheduledLiveStreams';
import { getOffering } from 'services/offerings';

export interface LiveStreamItem {
  slug: string;
  name: string;
  logoUrl: string | null;
  streamId?: string;
}

export interface UpcomingStreamItem {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  startDate: string;
  dayLabel: string;
  timeLabel: string;
}

export interface UpcomingGroup {
  key: string;
  headerLabel: string;
  items: UpcomingStreamItem[];
}

interface LiveCall {
  call?: {
    id?: string;
    custom?: { title?: string; slug?: string };
    ended_at?: string | null;
  };
}

interface OfferingSummary {
  name?: string;
  logoUrl?: string | null;
}

// Cap how many offerings we resolve in a single render to keep the
// per-slug useQueries fan-out bounded.
const MAX_OFFERINGS = 30;

const dayHeaderLabel = (date: dayjs.Dayjs, today: dayjs.Dayjs): string => {
  if (date.isSame(today, 'day')) return 'TODAY';
  if (date.isSame(today.add(1, 'day'), 'day')) return 'TOMORROW';
  return date.format('MMM D').toUpperCase();
};

const dayShortLabel = (date: dayjs.Dayjs, today: dayjs.Dayjs): string => {
  if (date.isSame(today, 'day')) return 'Today';
  if (date.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow';
  return date.format('MMM D');
};

interface UseLiveStreamsDataOptions {
  // When false, skip the per-offering lookups. Used by the Explore page to
  // decide tab visibility (`hasAny`) without fanning out a getOffering call
  // per slug on every load — the tab itself mounts with the default (true).
  withOfferings?: boolean;
  // When false, skip the live/scheduled polling entirely. Used by the Explore
  // page to avoid fetching for V2 users, who never see the tab.
  enabled?: boolean;
}

const useLiveStreamsData = ({
  withOfferings = true,
  enabled = true,
}: UseLiveStreamsDataOptions = {}) => {
  const { data: liveData, isLoading: liveLoading } = useGetLiveStreams({ enabled }) as {
    data: { calls?: LiveCall[] } | undefined;
    isLoading: boolean;
  };
  const { data: scheduledData, isLoading: scheduledLoading } = useGetScheduledLiveStreams({
    enabled,
  });

  const liveCalls = useMemo(() => liveData?.calls ?? [], [liveData]);
  const scheduledList = useMemo(() => scheduledData?.list ?? [], [scheduledData]);

  const liveBySlug = useMemo(() => {
    const map = new Map<string, { slug: string; streamId?: string }>();
    liveCalls.forEach((c) => {
      const slug = c.call?.custom?.slug;
      if (slug && !map.has(slug)) {
        map.set(slug, { slug, streamId: c.call?.id });
      }
    });
    return map;
  }, [liveCalls]);

  const upcomingRaw = useMemo(() => {
    const now = dayjs();
    return scheduledList
      .filter(
        (s) =>
          s.offeringSlug &&
          s.startDate &&
          !s.startedAt &&
          !s.endedAt &&
          !s.cancelledAt &&
          dayjs(s.startDate).isAfter(now),
      )
      .slice()
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());
  }, [scheduledList]);

  const orderedSlugs = useMemo(() => {
    const slugs: string[] = [];
    const seen = new Set<string>();
    const push = (slug?: string) => {
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      slugs.push(slug);
    };
    liveBySlug.forEach((v) => push(v.slug));
    upcomingRaw.forEach((s) => push(s.offeringSlug));
    return slugs.slice(0, MAX_OFFERINGS);
  }, [liveBySlug, upcomingRaw]);

  // `useQueries` returns a fresh array reference every render, so deriving the
  // slug->offering map in a downstream useMemo keyed on that array would
  // recompute on every render. Use `combine` (memoized via useCallback) so the
  // map is recomputed only when the slug list or the underlying query data
  // actually changes.
  const combineOfferings = useCallback(
    (results: Array<{ data?: OfferingSummary }>) => {
      const map = new Map<string, OfferingSummary>();
      orderedSlugs.forEach((slug, index) => {
        const offering = results[index]?.data;
        if (offering) map.set(slug, offering);
      });
      return map;
    },
    [orderedSlugs],
  );

  const offeringBySlug = useQueries({
    queries: orderedSlugs.map((slug) => ({
      queryKey: ['offering', slug],
      queryFn: () => getOffering({ slug }),
      enabled: enabled && withOfferings && !!slug,
    })),
    combine: combineOfferings,
  });

  const liveItems = useMemo<LiveStreamItem[]>(
    () =>
      Array.from(liveBySlug.values()).map(({ slug, streamId }) => {
        const offering = offeringBySlug.get(slug);
        return {
          slug,
          streamId,
          name: offering?.name || slug,
          logoUrl: offering?.logoUrl ?? null,
        };
      }),
    [liveBySlug, offeringBySlug],
  );

  const upcomingGroups = useMemo<UpcomingGroup[]>(() => {
    const today = dayjs().startOf('day');
    const groups = new Map<string, UpcomingGroup>();
    upcomingRaw.forEach((s) => {
      const date = dayjs(s.startDate);
      const key = date.format('YYYY-MM-DD');
      const offering = offeringBySlug.get(s.offeringSlug);
      const item: UpcomingStreamItem = {
        id: s.id,
        slug: s.offeringSlug,
        name: offering?.name || s.offeringSlug,
        logoUrl: offering?.logoUrl ?? null,
        startDate: s.startDate,
        dayLabel: dayShortLabel(date, today),
        timeLabel: date.format('h:mm A'),
      };
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(key, { key, headerLabel: dayHeaderLabel(date, today), items: [item] });
      }
    });
    return Array.from(groups.values());
  }, [upcomingRaw, offeringBySlug]);

  const liveCount = liveItems.length;
  const upcomingCount = upcomingRaw.length;

  return {
    liveItems,
    upcomingGroups,
    liveCount,
    upcomingCount,
    hasAny: liveCount + upcomingCount > 0,
    isLoading: liveLoading || scheduledLoading,
  };
};

export default useLiveStreamsData;
